/**
 * Email verification page component
 */

import { useEffect, useState, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { CheckCircle, XCircle, Loader2, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authApi, AuthApiError } from '../../shared/auth';
import { setupWizardApi } from '../../shared/api/setupWizardApi';
import { eraseFragmentToken, readFragmentToken } from '../../shared/api/publicToken';

type VerificationStatus = 'verifying' | 'success' | 'already_verified' | 'error';

export function VerifyEmailPage() {
  const { t } = useTranslation(['auth', 'common']);
  // The verification credential arrives in the URL FRAGMENT (`#token=…`), which
  // the browser never transmits — so it reaches no access log. Read once as a
  // lazy initializer (pure: StrictMode calls it twice); the erase is its own
  // effect below. There is deliberately no `?token=` fallback — honouring one
  // would keep the leak alive for exactly the links this protects.
  const [token] = useState(readFragmentToken);
  const [status, setStatus] = useState<VerificationStatus>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  // When the sysadmin approval gate is on, a verified email still doesn't mean
  // the account can be used yet — it's reviewed before activation. Say so here
  // rather than implying full access, but only when the gate is actually on.
  const [requiresApproval, setRequiresApproval] = useState(false);
  const verificationAttempted = useRef(false);

  useEffect(() => {
    setupWizardApi
      .getPublicTenantApprovalRequired()
      .then((gate) => setRequiresApproval(gate.require_tenant_approval))
      .catch(() => setRequiresApproval(false));
  }, []);

  useEffect(() => {
    eraseFragmentToken();
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t('auth:verification.invalid', 'Invalid verification link.'));
      return;
    }

    // Prevent duplicate API calls (React Strict Mode runs effects twice)
    if (verificationAttempted.current) {
      return;
    }
    verificationAttempted.current = true;

    const verifyEmail = async () => {
      try {
        const response = await authApi.verifyEmail({ token });
        setStatus('success');
        if (response.email) {
          setEmail(response.email);
        }
      } catch (err) {
        if (err instanceof AuthApiError) {
          // Extract email from error details if available
          if (err.details?.email) {
            setEmail(err.details.email);
          }

          if (err.code === 'ALREADY_VERIFIED') {
            // Show friendly "already verified" state instead of error
            setStatus('already_verified');
          } else {
            setStatus('error');
            if (err.code === 'TOKEN_EXPIRED') {
              setErrorMessage(t('auth:verification.expired', 'Verification link has expired. Please register again.'));
            } else if (err.code === 'INVALID_TOKEN') {
              setErrorMessage(t('auth:verification.invalid', 'Invalid verification link.'));
            } else {
              setErrorMessage(err.message || t('auth:verification.failed', 'Verification failed. Please try again.'));
            }
          }
        } else {
          setStatus('error');
          setErrorMessage(t('auth:verification.failed', 'Verification failed. Please try again.'));
        }
      }
    };

    verifyEmail();
  }, [token, t]);

  // Build login URL with email parameter if available
  const loginUrl = email ? `/login?email=${encodeURIComponent(email)}` : '/login';

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-[450px] w-full shadow-lg">
        <CardContent className="p-8 text-center space-y-6">
          <div className="flex justify-center">
            <img src="/panluma-logo.png" alt={t('common:app.name')} className="h-24" />
          </div>

          {status === 'verifying' && (
            <div className="py-8 space-y-4">
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground text-sm">
                {t('auth:verification.checking', 'Verifying your email...')}
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <div className="space-y-2 text-sm">
                <h2 className="text-xl font-semibold">
                  {t('auth:verification.title', 'Email Verified')}
                </h2>
                <Alert variant="default" className="bg-green-50 border-green-200 text-green-800">
                  <AlertDescription>
                    {requiresApproval
                      ? t('auth:verification.successPendingApproval', 'Your email is verified. You can sign in to finish setting up your account.')
                      : t('auth:verification.success', 'Your email has been verified. You can now log in.')}
                  </AlertDescription>
                </Alert>
                {requiresApproval && (
                  <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-left text-foreground">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                    <span>{t('auth:verification.approvalPendingNote', "Your account will be reviewed by our team before activation — we'll email you once it's approved.")}</span>
                  </div>
                )}
              </div>
              <Button
                asChild
                size="lg"
                className="w-full h-11"
              >
                <RouterLink to={loginUrl}>
                  {t('auth:verification.goToLogin', 'Go to Login')}
                </RouterLink>
              </Button>
            </div>
          )}

          {status === 'already_verified' && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <CheckCircle className="h-16 w-16 text-green-500" />
              </div>
              <div className="space-y-2 text-sm">
                <h2 className="text-xl font-semibold">
                  {t('auth:verification.alreadyVerifiedTitle', 'Already Verified')}
                </h2>
                <Alert variant="default" className="bg-green-50 border-green-200 text-green-800">
                  <AlertDescription>
                    {requiresApproval
                      ? t('auth:verification.alreadyVerifiedPendingApproval', 'Your email is verified. You can sign in to finish setting up your account.')
                      : t('auth:verification.alreadyVerified', 'Your email has already been verified. You can log in to your account.')}
                  </AlertDescription>
                </Alert>
                {requiresApproval && (
                  <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-left text-foreground">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                    <span>{t('auth:verification.approvalPendingNote', "Your account will be reviewed by our team before activation — we'll email you once it's approved.")}</span>
                  </div>
                )}
              </div>
              <Button
                asChild
                size="lg"
                className="w-full h-11"
              >
                <RouterLink to={loginUrl}>
                  {t('auth:verification.goToLogin', 'Go to Login')}
                </RouterLink>
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <XCircle className="h-16 w-16 text-destructive" />
              </div>
              <div className="space-y-2 text-sm">
                <h2 className="text-xl font-semibold">
                  {t('auth:verification.failedTitle', 'Verification Failed')}
                </h2>
                <Alert variant="destructive">
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  asChild
                  size="lg"
                  className="w-full h-11"
                >
                  <RouterLink to="/register">
                    {t('auth:verification.registerAgain', 'Register Again')}
                  </RouterLink>
                </Button>
                <Button
                  variant="outline"
                  asChild
                  size="lg"
                  className="w-full h-11"
                >
                  <RouterLink to={loginUrl}>
                    {t('auth:verification.goToLogin', 'Go to Login')}
                  </RouterLink>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
